import type { IConfig } from 'config';

import * as Sentry from '@sentry/browser';

/**
 * Singleton instance of Sentry after it's been initialized with our settings.
 * We hold reference to it at the module-level to prevent initializing it more
 * than once, since it's proven to cause some issues.
 */
let instance: typeof Sentry | null = null;

/**
 * This file is exclusively for reporting errors from a browser to Sentry.
 *
 * Why not use `lib/sentry.js`? As @sentry/node requires the `fs` Node module, loading @sentry/browser within the same
 * file causes `fs is undefined` errors in the browser even when doing conditional loading as Webpack packages up the
 * whole thing.
 */
const sentryBrowser = (component = 'dash', config: Partial<IConfig> = {}) => {
  if (!config.sentry || !config.sentry.enabled || !config.sentry.dsn || !component) return Sentry;

  // Prevent initializing Sentry more than once.
  if (instance) return instance;

  const environment = (process && process.env && process.env.NODE_ENV) || 'development';

  // Wrap init in try/catch to prevent unexpected issues from breaking the app.
  try {
    Sentry.init({
      /**
       * Disabling this option is the only sure way to prevent Sentry from
       * throwing an error when calling `Sentry.init()`. The other clue is that
       * whenever amplitude is enabled with a valid API key under
       * `packages/react/src/core/hooks/useAmplitude.tsx`, Sentry would throw
       * this error. But with amplitude either disabled or enabled with an empty
       * API key, no error is thrown. Its possible there's some conflict or side
       * effect when amplitude is running alongside Sentry.
       * @todo Figure out what we lose by disabling this and try to re-enable it
       * without throwing a `session.update() is not defined` error.
       * @link https://linear.app/readme-io/issue/RM-13936/figure-out-why-autosessiontracking-doesnt-work
       */
      autoSessionTracking: false,
      dsn: config.sentry?.dsn,
      environment,
      release: config.releaseVersion || '',
      // Filter bug traffic to Sentry -> Only allow errors originating from our own bundles
      // Ideally removes errors from user included script tags
      // Note: we'll be inclusive of any readme domains, not just cdn.{domain} URLs
      allowUrls: [new URL(config.asset_url || '').origin, /https:\/\/[^/]*readme.(?:com|io)(?:\/|$)/],
      initialScope: { tags: { 'readme-component': component } },
    });
  } catch (error) {
    console.error('Error occurred when calling "Sentry.init()"\n', error);
  } finally {
    instance = Sentry;
  }

  // Whenever sentry is consumed, make sure to keep its scope updated based on
  // the `component` passed in.
  if (component) {
    Sentry.configureScope(scope => {
      scope.update({ tags: { 'readme-component': component } });
    });
  }

  return instance;
};

export default sentryBrowser;
